import styles from "./AddParkingSpace.module.scss";
import { TiMinus, TiPlus } from "react-icons/ti";
import { Switch } from "react-animated-switch";
import React from "react";

type ParkingOption = {
  parkingId: number;
  amenityName: string;
  noOfCars: number;
  noOfBicycle: number;
  availableNoOfCars: number;
  availableNoOfBicycle: number;
  costCar: number;
  costBicycle: number;
};

type ParkingSelection = {
  parkingId: string;
  noofCars: number;
  noofBicycle: number;
  notifyParking: boolean;
};

interface Props {
  parkingOptions?: ParkingOption[];
  parkingSelections: ParkingSelection[];
  setParkingSelections: React.Dispatch<
    React.SetStateAction<ParkingSelection[]>
  >;
}

const AddParkingSpace = (props: Props) => {
  const { parkingOptions, parkingSelections, setParkingSelections } = props;

  const getSel = (parkingId: number) =>
    parkingSelections.find((s) => s.parkingId === String(parkingId)) ?? {
      parkingId: String(parkingId),
      noofCars: 0,
      noofBicycle: 0,
      notifyParking: false,
    };

  const updateSel = (parkingId: number, patch: Partial<ParkingSelection>) => {
    setParkingSelections((prev) => {
      const id = String(parkingId);

      const i = prev.findIndex((s) => s.parkingId === id);

      const cur =
        i >= 0
          ? prev[i]
          : {
              parkingId: id,
              noofCars: 0,
              noofBicycle: 0,
              notifyParking: false,
            };

      const next = { ...cur, ...patch };

      return i >= 0
        ? prev.map((s, j) => (j === i ? next : s))
        : [...prev, next];
    });
  };

  const changeCount = (
    opt: ParkingOption,
    type: "car" | "bicycle",
    delta: number,
  ) => {
    const s = getSel(opt.parkingId);

    if (type === "car") {
      const max = opt.noOfCars ?? 0;
      const next = s.noofCars + delta;

      if (next >= 0 && next <= max) {
        updateSel(opt.parkingId, { noofCars: next });
      }
    } else {
      const max = opt.noOfBicycle ?? 0;
      const next = s.noofBicycle + delta;

      if (next >= 0 && next <= max) {
        updateSel(opt.parkingId, { noofBicycle: next });
      }
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <p className={styles.heading}>Add Parking</p>
      </div>

      {parkingOptions?.map((opt) => {
        const s = getSel(opt.parkingId);

        const overCar = s.noofCars > (opt.availableNoOfCars ?? 0);

        const overBike = s.noofBicycle > (opt.availableNoOfBicycle ?? 0);

        const noCarAvailable = (opt.availableNoOfCars ?? 0) === 0;

        const noBikeAvailable = (opt.availableNoOfBicycle ?? 0) === 0;

        // show notify only when selected count exceeds available
        const showNotifySwitch = overCar || overBike;

        return (
          <div key={opt.parkingId} className={styles.parkingGroup}>
            {opt.amenityName && (
              <p className={styles.parkingGroupHeading}>{opt.amenityName}</p>
            )}

            <div className={styles.parkingContainer}>
              {/* CAR PARKING */}
              <div className={styles.contentContainer}>
                {(overCar || noCarAvailable) && (
                  <div className={styles.notAvailableContainer}>
                    <p className={styles.label}>Unavailable</p>
                  </div>
                )}

                <div className={styles.contentInnerContainer}>
                  <div className={styles.labelContainer}>
                    <p className={styles.label}>Car Parking</p>
                  </div>

                  <div className={styles.counterContainer}>
                    <button
                      className={styles.counterButton}
                      disabled={noCarAvailable}
                      onClick={() => changeCount(opt, "car", -1)}
                    >
                      <TiMinus />
                    </button>

                    <p className={styles.counterValue}>{s.noofCars}</p>

                    <button
                      className={styles.counterButton}
                      disabled={noCarAvailable}
                      onClick={() => changeCount(opt, "car", 1)}
                    >
                      <TiPlus />
                    </button>
                  </div>

                  <div className={styles.priceContainer}>
                    <p>$ {(opt.costCar ?? 0) * s.noofCars}</p>
                  </div>
                </div>
              </div>

              {/* BICYCLE PARKING */}
              <div className={styles.contentContainer}>
                {(overBike || noBikeAvailable) && (
                  <div className={styles.notAvailableContainer}>
                    <p className={styles.label}>Unavailable</p>
                  </div>
                )}

                <div className={styles.contentInnerContainer}>
                  <div className={styles.labelContainer}>
                    <p className={styles.label}>Bicycle Parking</p>
                  </div>

                  <div className={styles.counterContainer}>
                    <button
                      className={styles.counterButton}
                      disabled={noBikeAvailable}
                      onClick={() => changeCount(opt, "bicycle", -1)}
                    >
                      <TiMinus />
                    </button>

                    <p className={styles.counterValue}>{s.noofBicycle}</p>

                    <button
                      className={styles.counterButton}
                      disabled={noBikeAvailable}
                      onClick={() => changeCount(opt, "bicycle", 1)}
                    >
                      <TiPlus />
                    </button>
                  </div>

                  <div className={styles.priceContainer}>
                    <p>$ {(opt.costBicycle ?? 0) * s.noofBicycle}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SINGLE NOTIFY SWITCH */}
            {showNotifySwitch && (
              <div className={styles.noticeContainer}>
                <div className={styles.switchContainer}>
                  <Switch
                    inActiveTrackColor="#DFDFDF"
                    activeTrackColor="#C0F496"
                    inActiveThumbColor="#000000"
                    activeThumbColor="#000000"
                    checked={s.notifyParking}
                    onChange={(val) =>
                      updateSel(opt.parkingId, {
                        notifyParking: val,
                      })
                    }
                  />
                </div>

                <p className={styles.label}>
                  Notify me when parking is available
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AddParkingSpace;
